import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { SmartLink } from "../lib/navLink.jsx";

// Full icon class incl. the base set (`bi` Bootstrap Icons, `ti` Tabler Icons),
// so keys can draw from whichever set has the glyph.
const iconClass = {
  boxes: "bi bi-box-seam",
  clipboard: "bi bi-clipboard2-pulse",
  grid: "bi bi-grid-3x3-gap",
  headset: "bi bi-headset",
  shield: "bi bi-shield-plus",
  heartPulse: "bi bi-heart-pulse",
  droplet: "bi bi-droplet",
  kit: "bi bi-bag",
};

// Bootstrap Icons has no shoe glyph, and the Tabler shoe webfont renders with a
// heavier stroke (2px on a 24 grid) than the Bootstrap Icons around it (~1px on
// a 16 grid). A webfont glyph's stroke can't be thinned via CSS, so inline the
// shoe and set stroke-width to match the lighter Bootstrap optical weight.
function ShoeIcon({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M4 6h5.426a1 1 0 0 1 .863 .496l1.064 1.823a3 3 0 0 0 1.896 1.407l4.677 1.114a4 4 0 0 1 3.074 3.89v2.27a1 1 0 0 1 -1 1h-16a1 1 0 0 1 -1 -1v-10a1 1 0 0 1 1 -1z" />
      <path d="M14 13l1 -2" />
      <path d="M8 18v-1a4 4 0 0 0 -4 -4h-1" />
      <path d="M10 12l1.5 -3" />
    </svg>
  );
}

// Gradient bg for each icon key — gives each card a distinct feel
const imgAreaClass = {
  boxes:      "pmp-scard-img--blue",
  clipboard:  "pmp-scard-img--teal",
  grid:       "pmp-scard-img--green",
  headset:    "pmp-scard-img--orange",
  shield:     "pmp-scard-img--indigo",
  heartPulse: "pmp-scard-img--rose",
  droplet:    "pmp-scard-img--teal",
  footwear:   "pmp-scard-img--green",
  kit:        "pmp-scard-img--orange",
};

/**
 * Service capability cards — vertical layout with image/icon area on top,
 * large border-radius, gradient border on hover. Matches AWS industry cards.
 */
export default function InfoCards({ cards, section }) {
  if (!cards?.length) return null;
  const kicker = section?.kicker ?? "Why teams choose PMP";
  const title =
    section?.title ?? "Four wholesale categories, one accountable partner";

  return (
    <section className="pmp-cards-section" id="capabilities">
      <Container>
        <div className="d-flex align-items-end justify-content-between flex-wrap gap-3 mb-5">
          <div>
            <p className="pmp-kicker mb-2">{kicker}</p>
            <h2 className="h3 fw-bold mb-0">{title}</h2>
          </div>
        </div>
        <Row className="g-4">
          {cards.map((c, i) => (
            <Col key={`${c.title}-${i}`} md={6} xl={3}>
              <SmartLink
                href={c.linkHref || "#catalog"}
                className="pmp-scard-link d-block h-100 text-decoration-none"
              >
                <div className="pmp-scard">
                  {/* Image / icon area */}
                  <div
                    className={`pmp-scard-img ${imgAreaClass[c.iconKey] || "pmp-scard-img--blue"}`}
                  >
                    {c.imageUrl ? (
                      <img
                        src={c.imageUrl}
                        alt={c.imageAlt ?? c.title}
                        className="w-100 h-100 object-fit-cover"
                        style={{ position: "absolute", inset: 0 }}
                      />
                    ) : c.iconKey === "footwear" ? (
                      <ShoeIcon className="pmp-scard-shoe" />
                    ) : (
                      <span
                        className={iconClass[c.iconKey] || "bi bi-circle"}
                        aria-hidden="true"
                      />
                    )}
                  </div>

                  {/* Content */}
                  <div className="pmp-scard-body">
                    <h3 className="pmp-scard-title">{c.title}</h3>
                    <p className="pmp-scard-desc">{c.body}</p>
                    <div className="pmp-scard-footer">
                      <span className="pmp-scard-arrow">
                        {c.linkLabel ? `${c.linkLabel} ` : ""}
                        &#x2192;
                      </span>
                    </div>
                  </div>
                </div>
              </SmartLink>
            </Col>
          ))}
        </Row>
      </Container>
    </section>
  );
}
